import type { JSX } from 'react';
import { FileSpreadsheet, Files, Lock, Notebook } from 'lucide-react';
import { Container } from '@components/ui/Container';
import { RevealOnView } from '@components/ui/RevealOnView';
import { SectionHeading } from '@components/ui/SectionHeading';
import { landingCopy } from '@features/landing/data/landing.pt';
import type { ProblemaItem } from '@features/landing/data/types';

const ICON_BY_KEY: Record<ProblemaItem['iconKey'], JSX.Element> = {
  spreadsheet: <FileSpreadsheet className="h-6 w-6" aria-hidden="true" />,
  lock: <Lock className="h-6 w-6" aria-hidden="true" />,
  manual: <Notebook className="h-6 w-6" aria-hidden="true" />,
  scattered: <Files className="h-6 w-6" aria-hidden="true" />,
};

const STAGGER_DELAY_MS = 100;

/**
 * Secao "O problema (a dor)" (SECT-03 — PRD §4.4).
 *
 * Lista 4 dores tipicas de PME que justificam contratar a Omanna. Layout:
 *  - Section header (eyebrow + title) — `align="center"` para criar
 *    momentum emocional ("Sua empresa ainda funciona assim?" funciona melhor
 *    centralizado, como pergunta direta ao leitor)
 *  - Grid 1 coluna mobile / 2 colunas desktop com 4 cards
 *  - Cada card: icone (lucide) em circle background brand-100, texto curto
 *  - Frase de transicao centralizada em font-display, brand-700, abaixo do
 *    grid: "A Omanna existe pra acabar com isso."
 *
 * Anchor: `#problema` (nao tem entrada propria no Navbar — esta secao e
 * narrativa, nao destino direto de navegacao).
 * Background: `bg-neutral-50` para alternar com QuemSomos (`bg-white`) e
 * Hero (`bg-neutral-50`), criando ritmo white-cream-white-cream.
 *
 * Reveal: header + 4 cards + transition phrase com stagger CSS-only.
 *
 * Acessibilidade:
 *  - `<section id="problema" aria-labelledby="problema-heading">`
 *  - Cards usam `<article>` para identificar como entidade narrativa
 *  - Icons decorativos (aria-hidden) — o texto e o rotulo semantico
 */
export const Problema = (): JSX.Element => {
  const { problema } = landingCopy;

  return (
    <section
      id="problema"
      aria-labelledby="problema-heading"
      className="bg-neutral-50 py-20 lg:py-28"
    >
      <Container>
        <RevealOnView>
          <SectionHeading
            as="h2"
            eyebrow={problema.eyebrow}
            title={problema.title}
            align="center"
            className="mx-auto max-w-3xl"
            titleId="problema-heading"
          />
        </RevealOnView>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:gap-8">
          {problema.items.map((item, index) => (
            <RevealOnView key={item.iconKey} delayMs={STAGGER_DELAY_MS * (index + 1)} slide="up">
              <article className="flex h-full items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md sm:p-7">
                <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  {ICON_BY_KEY[item.iconKey]}
                </div>
                <p className="text-base leading-relaxed text-neutral-800">{item.text}</p>
              </article>
            </RevealOnView>
          ))}
        </div>

        <RevealOnView delayMs={STAGGER_DELAY_MS * 5} className="mt-14 text-center">
          <p className="font-display text-2xl font-bold text-brand-700 sm:text-3xl">
            {problema.transition}
          </p>
        </RevealOnView>
      </Container>
    </section>
  );
};
